import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  FilterAttribute,
  FilterOperator,
  IndividualSessionFilterValue,
  OperatorType,
  SessionFilter,
  SessionQuickFilter,
} from '../../components/SessionFilters/model'
import { selectAuthenticatedUser } from '../auth/authSlice'

export interface FilterTag {
  attr: FilterAttribute
  val: string | boolean | null
}

interface SessionFilterState {
  isLoading: boolean
  userIds: string[]
  contactIds: string[]
  dateRange: [string, string] | null
  meetingTemplateIds: string[]
  tags: FilterTag[]
  meetingKind: string | null
  meetingSources: string[]
  filter?: SessionQuickFilter | null
  pageSize: number | null
  page: number | null
}

const initialState: SessionFilterState = {
  isLoading: true,
  userIds: [],
  contactIds: [],
  dateRange: null,
  meetingTemplateIds: [],
  tags: [],
  meetingKind: null,
  meetingSources: [],
  filter: null,
  pageSize: null,
  page: null,
}

export const sessionFilterSlice = createSlice({
  name: 'sessionFilter',
  initialState,
  reducers: {
    setSessionFilterIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      if (action.payload) {
        state.filter = null
      }
    },
    setSessionFilterUserIds: (state, action: PayloadAction<string[]>) => {
      state.userIds = action.payload
      state.filter = null
    },
    setSessionFilterContactIds: (state, action: PayloadAction<string[]>) => {
      state.contactIds = action.payload
      state.filter = null
    },
    setSessionFilterDateRange: (state, action: PayloadAction<[string, string] | null>) => {
      state.dateRange = action.payload
      state.filter = null
    },
    setSessionFilterMeetingTemplateIds: (state, action: PayloadAction<string[]>) => {
      state.meetingTemplateIds = action.payload
      state.filter = null
    },
    setSessionFilterTags: (state, action: PayloadAction<FilterTag[]>) => {
      state.tags = action.payload
      state.filter = null
    },
    setSessionFilterMeetingKind: (state, action: PayloadAction<string | null>) => {
      state.meetingKind = action.payload
      state.filter = null
    },
    setSessionFilterMeetingSources: (state, action: PayloadAction<string[]>) => {
      state.meetingSources = action.payload
      state.filter = null
    },
    setSessionQuickFilter: (state, action: PayloadAction<SessionQuickFilter>) => {
      const individualFilters = _getIndividualFilterValues(action.payload.filter)
      const userFilter = individualFilters.find((filter) => filter.attr === FilterAttribute.USER_ID)
      const contactFilter = individualFilters.find((filter) => filter.attr === FilterAttribute.CONTACT_ID)
      const startDateFilter = individualFilters.find((filter) => filter.attr === FilterAttribute.STARTED)
      const endDateFilter = individualFilters.find((filter) => filter.attr === FilterAttribute.FINISHED)
      const meetingTemplateIdsFilter = individualFilters.filter((filter) => filter.attr === FilterAttribute.MEETING_TYPE)
      const filterTagsFilter = individualFilters.filter(
        (filter) =>
          filter.attr === FilterAttribute.BOT_SENT ||
          filter.attr === FilterAttribute.PROCESSED ||
          filter.attr === FilterAttribute.ZOCKS_ANALYZED ||
          filter.attr === FilterAttribute.ASSISTANT_JOINED,
      )
      const meetingKindFilter = individualFilters.find((filter) => filter.attr === FilterAttribute.KIND)
      const meetingSourcesFilter = individualFilters.filter((filter) => filter.attr === FilterAttribute.SOURCE_TYPE)
      state.userIds = userFilter ? (userFilter.val as string).split(',') : []
      state.contactIds = contactFilter ? (contactFilter.val as string).split(',') : []
      state.dateRange = startDateFilter && endDateFilter ? [startDateFilter.val as string, endDateFilter.val as string] : null
      state.meetingTemplateIds = meetingTemplateIdsFilter.map((filter) => filter.val as string)

      state.filter = { ...action.payload }
      state.tags = filterTagsFilter.map((filter) => {
        return { attr: filter.attr, val: filter.val }
      })
      state.meetingKind = meetingKindFilter ? (meetingKindFilter.val as string) : null
      state.meetingSources = meetingSourcesFilter.map((filter) => filter.val as string)
    },
    setPage: (state, action: { payload: number | null; type: string }) => {
      state.page = action.payload
    },
    setPageSize: (state, action: { payload: number | null; type: string }) => {
      state.pageSize = action.payload
    },
    resetSessionFilter: (state, action: PayloadAction<string[]>) => {
      state.userIds = action.payload
      state.contactIds = []
      state.dateRange = null
      state.meetingTemplateIds = []
      state.filter = null
      state.tags = []
      state.meetingKind = null
      state.meetingSources = []
    },
  },
})

const _getIndividualFilterValues = (filter: SessionFilter): IndividualSessionFilterValue[] => {
  if (filter.type === 'individual') {
    return [filter.filter]
  } else if (filter.type === 'composite') {
    return filter.filters.reduce((acc: IndividualSessionFilterValue[], f) => {
      return acc.concat(_getIndividualFilterValues(f))
    }, [])
  }
  return []
}

export const {
  setSessionFilterIsLoading,
  setSessionFilterTags,
  setSessionFilterUserIds,
  setSessionFilterContactIds,
  setSessionQuickFilter,
  setSessionFilterDateRange,
  setSessionFilterMeetingTemplateIds,
  setSessionFilterMeetingKind,
  setSessionFilterMeetingSources,
  setPage,
  setPageSize,
  resetSessionFilter,
} = sessionFilterSlice.actions

export const selectSessionFilterLoading = (state: RootState) => state.sessionFilter.isLoading
export const selectSessionFilterUserIds = (state: RootState) => state.sessionFilter.userIds
export const selectSessionFilterContactIds = (state: RootState) => state.sessionFilter.contactIds
export const selectSessionFilterDateRange = (state: RootState) => state.sessionFilter.dateRange
export const selectSessionFilterMeetingTemplateIds = (state: RootState) => state.sessionFilter.meetingTemplateIds
export const selectSessionFilter = (state: RootState) => state.sessionFilter.filter
export const selectSessionFilterTags = (state: RootState) => state.sessionFilter.tags
export const selectSessionFilterMeetingKind = (state: RootState) => state.sessionFilter.meetingKind
export const selectSessionFilterMeetingSources = (state: RootState) => state.sessionFilter.meetingSources
export const selectSessionFilterPage = (state: RootState) => state.sessionFilter.page
export const selectSessionFilterPageSize = (state: RootState) => state.sessionFilter.pageSize

export const selectUserIdSessionFilter = createSelector(
  [selectSessionFilterUserIds, selectAuthenticatedUser],
  (sessionFilterUserIds, currentUser): SessionFilter => {
    if (!currentUser) {
      throw Error('Something went wrong!')
    }
    const userIds = sessionFilterUserIds.length ? sessionFilterUserIds : [currentUser.id]
    return {
      type: 'individual',
      filter: { op: FilterOperator.IN, val: userIds.join(','), attr: FilterAttribute.USER_ID },
    }
  },
)

export const selectContactIdSessionFilter = createSelector(
  [selectSessionFilterContactIds, selectAuthenticatedUser],
  (sessionFilterContactIds, currentUser): SessionFilter => {
    const contactIds = sessionFilterContactIds.length ? sessionFilterContactIds : ['{all}']
    return {
      type: 'individual',
      filter: { op: FilterOperator.IN, val: contactIds.join(','), attr: FilterAttribute.CONTACT_ID },
    }
  },
)

export const selectDateRangeSessionFilter = createSelector([selectSessionFilterDateRange], (dateRange): SessionFilter | null => {
  if (!dateRange) {
    return null
  }
  const [startDate, endDate] = dateRange
  return {
    type: 'composite',
    operator: OperatorType.AND,
    filters: [
      { type: 'individual', filter: { op: FilterOperator.GT, attr: FilterAttribute.STARTED, val: startDate } },
      { type: 'individual', filter: { op: FilterOperator.LT, attr: FilterAttribute.FINISHED, val: endDate } },
    ],
  }
})

export const selectMeetingTemplateIdsSessionFilter = createSelector([selectSessionFilterMeetingTemplateIds], (meetingTemplateIds): SessionFilter | null => {
  if (!meetingTemplateIds.length) {
    return null
  }
  return {
    type: 'composite',
    operator: OperatorType.OR,
    filters: meetingTemplateIds.map((id) => {
      return { type: 'individual', filter: { op: FilterOperator.EQUALS, attr: FilterAttribute.MEETING_TYPE, val: id } }
    }),
  }
})

export const selectTagsSessionFilter = createSelector([selectSessionFilterTags], (tags): SessionFilter | null => {
  if (!tags.length) {
    return null
  }
  return {
    type: 'composite',
    operator: OperatorType.AND,
    filters: tags.map((tag) => {
      return { type: 'individual', filter: { op: FilterOperator.EQUALS, attr: tag.attr, val: tag.val } }
    }),
  }
})

export const selectMeetingKindSessionFilter = createSelector([selectSessionFilterMeetingKind], (kind): SessionFilter | null => {
  if (!kind) {
    return null
  }
  return {
    type: 'individual',
    filter: { op: FilterOperator.EQUALS, val: kind, attr: FilterAttribute.KIND },
  }
})

export const selectMeetingSourcesSessionFilter = createSelector([selectSessionFilterMeetingSources], (sources): SessionFilter | null => {
  if (!sources.length) {
    return null
  }
  return {
    type: 'composite',
    operator: OperatorType.OR,
    filters: sources.map((source) => {
      return {
        type: 'individual',
        filter: { op: FilterOperator.EQUALS, attr: FilterAttribute.SOURCE_TYPE, val: source },
      }
    }),
  }
})

export const selectMeetingKindSourceCompositeFilter = createSelector(
  [selectSessionFilterMeetingKind, selectSessionFilterMeetingSources],
  (kind, sources): SessionFilter | null => {
    if (!kind && !sources.length) {
      return null
    }
    return {
      type: 'composite',
      operator: OperatorType.OR,
      filters: [
        ...(kind
          ? [
              {
                type: 'individual',
                filter: { op: FilterOperator.EQUALS, val: kind, attr: FilterAttribute.KIND },
              } as SessionFilter,
            ]
          : []),
        ...sources.map((source) => {
          return {
            type: 'individual',
            filter: { op: FilterOperator.EQUALS, attr: FilterAttribute.SOURCE_TYPE, val: source },
          } as SessionFilter
        }),
      ],
    }
  },
)

export const selectCompositeSessionFilter = createSelector(
  [
    selectUserIdSessionFilter,
    selectContactIdSessionFilter,
    selectDateRangeSessionFilter,
    selectMeetingTemplateIdsSessionFilter,
    selectTagsSessionFilter,
    selectMeetingKindSourceCompositeFilter,
  ],
  (
    userIdSessionFilter,
    contactIdSessionFilter,
    dateRangeSessionFilter,
    meetingTemplateIdsSessionFilter,
    tagsSessionFilter,
    kindSourceCompositeFilter,
  ): SessionFilter => {
    return {
      filters: [
        userIdSessionFilter,
        contactIdSessionFilter,
        dateRangeSessionFilter,
        meetingTemplateIdsSessionFilter,
        tagsSessionFilter,
        kindSourceCompositeFilter,
      ].filter((f): f is SessionFilter => f !== null),
      operator: OperatorType.AND,
      type: 'composite',
    }
  },
)

export const selectPastMeetingsCompositeFilter = createSelector([selectCompositeSessionFilter], (compositeSessionFilter): SessionFilter => {
  return {
    filters: [
      compositeSessionFilter,
      {
        type: 'individual',
        filter: { op: FilterOperator.LTE, attr: FilterAttribute.FINISHED, val: '{now}' },
      },
    ],
    operator: OperatorType.AND,
    type: 'composite',
  }
})

export const selectLiveMeetingsCompositeFilter = createSelector([selectCompositeSessionFilter], (compositeSessionFilter): SessionFilter => {
  return {
    filters: [
      {
        filters: [compositeSessionFilter, { type: 'individual', filter: { op: FilterOperator.EQUALS, attr: FilterAttribute.USER_ID, val: '{user_id}' } }],
        operator: OperatorType.OR,
        type: 'composite',
      },
      {
        type: 'individual',
        filter: { op: FilterOperator.LTE, attr: FilterAttribute.STARTED, val: '{now+5m}' },
      },
      {
        filters: [
          { type: 'individual', filter: { op: FilterOperator.GT, attr: FilterAttribute.FINISHED, val: '{now}' } },
          { type: 'individual', filter: { op: FilterOperator.EQUALS, attr: FilterAttribute.FINISHED, val: null } },
        ],
        operator: OperatorType.OR,
        type: 'composite',
      },
    ],
    operator: OperatorType.AND,
    type: 'composite',
  }
})

export const selectUpcomingMeetingsCompositeFilter = createSelector([selectCompositeSessionFilter], (compositeSessionFilter): SessionFilter => {
  return {
    filters: [
      compositeSessionFilter,
      {
        type: 'individual',
        filter: { op: FilterOperator.GT, attr: FilterAttribute.STARTED, val: '{now+5m}' },
      },
    ],
    operator: OperatorType.AND,
    type: 'composite',
  }
})

export default sessionFilterSlice.reducer
